import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from '@/hooks/usePermissions';
import { empresaService } from '@/services/api';
import { Empresa } from '@/types';
import { AppLayout } from '@/components/layout/AppLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Building2, Loader2, LayoutGrid, Users, ShieldCheck, FolderTree, Clock, Power, Trash2 } from 'lucide-react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { maskCNPJ } from '@/lib/masks';
import { cn } from '@/lib/utils';
import {
  DadosEmpresaFields,
  DadosEmpresaValues,
  dadosEmpresaFromEmpresa,
  emptyDadosEmpresa,
} from '@/components/empresa/DadosEmpresaFields';

// Espelha backend/src/config/modules.ts (MODULE_KEYS) — usado só pra rotular
// modulos_habilitados aqui na tela, não é a fonte de verdade.
const MODULO_LABELS: Record<string, string> = {
  okrs: 'OKRs',
  projetos: 'Projetos',
  relatorios: 'Relatórios',
  auditoria: 'Auditoria',
  cronogramas: 'Cronogramas',
  organograma: 'Organograma',
  calendario: 'Calendário',
  fluxogramas: 'Fluxogramas',
  treinamentos: 'Treinamentos',
};

export default function MinhaEmpresa() {
  const { user, empresaAtiva, empresaFiltroId, setEmpresaFiltroId } = useAuth();
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  // Vindo de um clique na listagem de Empresas (superadmin) o :id da rota manda;
  // sem ele, cai no comportamento padrão: empresa ativa do header (admin/comum) ou
  // empresa selecionada no filtro global (superadmin) / própria empresa.
  const { id: idParam } = useParams<{ id?: string }>();
  const [empresa, setEmpresa] = useState<Empresa | null>(null);
  const [dadosEmpresa, setDadosEmpresa] = useState<DadosEmpresaValues>(emptyDadosEmpresa);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isToggleStatusOpen, setIsToggleStatusOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const empresaId = idParam ?? empresaAtiva?.id ?? empresaFiltroId ?? user?.empresa_id ?? null;

  // Ao navegar pra outra tela a partir de um card, fixa o filtro global de empresa
  // (superadmin) na empresa desta tela, senão a tela de destino cairia em "Todas as
  // empresas" (ou noutra empresa qualquer) em vez de mostrar os dados desta.
  const irParaComEmpresa = (path: string) => {
    if (empresa && user?.tipo === 'superadmin') {
      setEmpresaFiltroId(empresa.id);
    }
    navigate(path);
  };

  useEffect(() => {
    const load = async () => {
      if (!empresaId) {
        setIsLoading(false);
        return;
      }
      try {
        const res = await empresaService.getById(empresaId);
        if (res.success && res.data) {
          setEmpresa(res.data);
          setDadosEmpresa(dadosEmpresaFromEmpresa(res.data));
        } else if (!res.success) {
          toast.error(res.error || 'Erro ao carregar dados da empresa');
        }
      } catch {
        toast.error('Erro ao carregar dados da empresa');
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [empresaId]);

  // Somente admin e superadmin acessam esta tela
  if (user && user.tipo !== 'admin' && user.tipo !== 'superadmin') {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSave = async () => {
    if (!empresa) return;
    setIsConfirmOpen(false);
    setIsSaving(true);
    try {
      const result = await empresaService.update(empresa.id, dadosEmpresa);
      if (result.success && result.data) {
        toast.success('Dados da empresa atualizados');
        setEmpresa(result.data);
        setDadosEmpresa(dadosEmpresaFromEmpresa(result.data));
      } else {
        toast.error(result.error || 'Erro ao atualizar dados da empresa');
      }
    } catch {
      toast.error('Erro ao atualizar dados da empresa');
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!empresa) return;
    try {
      const result = await empresaService.toggleStatus(empresa.id);
      if (result.success && result.data) {
        toast.success(`Empresa ${result.data.status === 'ativa' ? 'ativada' : 'desativada'}`);
        setEmpresa(result.data);
      } else {
        toast.error(result.error || 'Erro ao alterar status da empresa');
      }
    } catch {
      toast.error('Erro ao alterar status da empresa');
    } finally {
      setIsToggleStatusOpen(false);
    }
  };

  const handleDelete = async () => {
    if (!empresa) return;
    try {
      const result = await empresaService.delete(empresa.id);
      if (result.success) {
        toast.success('Empresa excluída com sucesso');
        navigate('/empresas');
      } else {
        toast.error(result.error || 'Erro ao excluir empresa');
      }
    } catch {
      toast.error('Erro ao excluir empresa');
    } finally {
      setIsDeleteOpen(false);
    }
  };

  if (isLoading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="space-y-6">
        <div className="rounded-lg border bg-card p-4 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
            <Building2 className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-xl font-semibold">{empresa?.nome || 'Minha Empresa'}</h1>
            <p className="text-sm text-muted-foreground">
              Dados cadastrais da empresa
              {empresa?.cnpj && ` · CNPJ: ${maskCNPJ(empresa.cnpj)}`}
            </p>
          </div>
          {empresa && (
            <Badge variant={empresa.status === 'ativa' ? 'default' : 'secondary'} className="ml-auto">
              {empresa.status === 'ativa' ? 'Ativa' : 'Inativa'}
            </Badge>
          )}
        </div>

        {empresa && (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <Card
              className="cursor-pointer transition-colors hover:bg-muted/50"
              onClick={() => irParaComEmpresa('/usuarios')}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter') irParaComEmpresa('/usuarios'); }}
            >
              <CardContent className="p-4 flex items-center gap-3">
                <Users className="h-5 w-5 text-muted-foreground shrink-0" />
                <div>
                  <div className="text-2xl font-semibold">{empresa.usuario_count ?? 0}</div>
                  <div className="text-xs text-muted-foreground">Usuários</div>
                </div>
              </CardContent>
            </Card>
            <Card
              className="cursor-pointer transition-colors hover:bg-muted/50"
              onClick={() => irParaComEmpresa('/usuarios?tipo=admin')}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter') irParaComEmpresa('/usuarios?tipo=admin'); }}
            >
              <CardContent className="p-4 flex items-center gap-3">
                <ShieldCheck className="h-5 w-5 text-muted-foreground shrink-0" />
                <div>
                  <div className="text-2xl font-semibold">{empresa.admin_count ?? 0}</div>
                  <div className="text-xs text-muted-foreground">Admins</div>
                </div>
              </CardContent>
            </Card>
            <Card
              className="cursor-pointer transition-colors hover:bg-muted/50"
              onClick={() => irParaComEmpresa('/departamentos')}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter') irParaComEmpresa('/departamentos'); }}
            >
              <CardContent className="p-4 flex items-center gap-3">
                <FolderTree className="h-5 w-5 text-muted-foreground shrink-0" />
                <div>
                  <div className="text-2xl font-semibold">{empresa.departamento_count ?? 0}</div>
                  <div className="text-xs text-muted-foreground">Departamentos</div>
                </div>
              </CardContent>
            </Card>
            <Card
              className={cn(
                "transition-colors",
                hasPermission('auditoria') ? "cursor-pointer hover:bg-muted/50" : "cursor-default"
              )}
              onClick={hasPermission('auditoria') ? () => irParaComEmpresa('/auditoria') : undefined}
              role={hasPermission('auditoria') ? 'button' : undefined}
              tabIndex={hasPermission('auditoria') ? 0 : undefined}
              onKeyDown={hasPermission('auditoria') ? (e) => { if (e.key === 'Enter') irParaComEmpresa('/auditoria'); } : undefined}
            >
              <CardContent className="p-4 flex items-center gap-3">
                <Clock className="h-5 w-5 text-muted-foreground shrink-0" />
                <div>
                  <div className="text-sm font-semibold">
                    {empresa.ultima_atividade ? new Date(empresa.ultima_atividade).toLocaleDateString('pt-BR') : '—'}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Última atividade
                    {empresa.ultimo_modulo_ativo && ` · ${MODULO_LABELS[empresa.ultimo_modulo_ativo] || empresa.ultimo_modulo_ativo}`}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {!empresa ? (
          <Card>
            <CardContent className="py-10 text-center text-muted-foreground">
              Nenhuma empresa ativa encontrada.
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6 md:grid-cols-3">
            <Card className="md:col-span-2">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  Identificação e endereço
                </CardTitle>
                <CardDescription>Dados fiscais e de endereço da empresa</CardDescription>
              </CardHeader>
              <CardContent>
                <DadosEmpresaFields
                  values={dadosEmpresa}
                  onChange={(patch) => setDadosEmpresa((current) => ({ ...current, ...patch }))}
                  idPrefix="minha-empresa"
                />
              </CardContent>
              <CardFooter className="justify-end border-t pt-4">
                <Button onClick={() => setIsConfirmOpen(true)} disabled={isSaving}>
                  {isSaving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Salvando...
                    </>
                  ) : (
                    'Salvar alterações'
                  )}
                </Button>
              </CardFooter>
            </Card>

            <div className="md:col-span-1 flex flex-col gap-6">
              <Card className="flex-1 flex flex-col">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <LayoutGrid className="h-4 w-4" />
                    Módulos ativos
                  </CardTitle>
                  <CardDescription>
                    Funcionalidades habilitadas para esta empresa. Para alterar, entre em contato com o suporte.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {(empresa.modulos_habilitados || []).filter(m => m !== 'cronogramas').map(m => (
                      <Badge key={m} variant="outline" className="text-xs">
                        {MODULO_LABELS[m] || m}
                      </Badge>
                    ))}
                    {(empresa.modulos_habilitados || []).length === 0 && (
                      <span className="text-sm text-muted-foreground">Nenhum módulo habilitado</span>
                    )}
                  </div>
                </CardContent>
              </Card>

              {user?.tipo === 'superadmin' && (
                <Card className="flex-1 flex flex-col">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Ações da empresa</CardTitle>
                    <CardDescription>
                      Ativar/desativar acesso dos usuários ou excluir a empresa permanentemente.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="flex flex-col gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setIsToggleStatusOpen(true)}
                      className={cn(
                        'justify-start',
                        empresa.status === 'ativa'
                          ? 'text-destructive hover:text-destructive'
                          : 'text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300'
                      )}
                    >
                      <Power className="h-4 w-4 mr-2" />
                      {empresa.status === 'ativa' ? 'Desativar empresa' : 'Ativar empresa'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setIsDeleteOpen(true)}
                      className="justify-start text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Excluir empresa
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        )}
      </div>

      <AlertDialog open={isConfirmOpen} onOpenChange={setIsConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar alteração dos dados da empresa</AlertDialogTitle>
            <AlertDialogDescription>
              Isso vai atualizar CNPJ, Razão social, Nome fantasia e endereço de {empresa?.nome}. Deseja continuar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleSave}>Salvar alterações</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={isToggleStatusOpen} onOpenChange={setIsToggleStatusOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {empresa?.status === 'ativa' ? 'Desativar Empresa' : 'Ativar Empresa'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {empresa?.status === 'ativa'
                ? `Tem certeza que deseja desativar a empresa "${empresa?.nome}"? Os usuários não poderão mais acessar o sistema.`
                : `Deseja reativar a empresa "${empresa?.nome}"? Os usuários voltarão a ter acesso.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleToggleStatus}
              className={empresa?.status === 'ativa' ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90' : ''}
            >
              Confirmar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir Empresa</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir a empresa <strong>{empresa?.nome}</strong>?
              Esta ação não pode ser desfeita e todos os dados relacionados serão removidos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}
